import { useEffect, useMemo, useState } from "react";
import { Control, useForm } from "react-hook-form";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, HealthAndSafety } from "@mui/icons-material";
import { toast } from "react-toastify";

import { FormAutoComplete } from "@/components/form/FormAutoComplete";
import { FormTextInput } from "@/components/form/FormTextInput";
import { useOrganizations } from "@/services/fhir/organization/useOrganizations";
import { useInsurancePlans } from "@/services/fhir/insuranceplan/useInsurancePlans";
import { PostCoverage } from "@/services/fhir/coverage/PostCoverage";

export interface CoverageFormData {
  organization: { value: string; label: string } | null;
  insurancePlan: { value: string; label: string; display?: string } | null;
  cardNumber: string;
}

interface CoverageModalProps {
  open: boolean;
  isEditing: boolean;
  patientId: string;
  initialData?: Partial<CoverageFormData> & { id?: string };
  onClose: () => void;
  onSuccess: () => void;
}

function CoverageFormFields({
  control,
  isEditing,
  organizationOptions,
  organizationsLoading,
  organizationSearchTerm,
  setOrganizationSearchTerm,
  insurancePlanOptions,
  insurancePlansLoading,
  selectedOrganization,
}: {
  control: Control<CoverageFormData>;
  isEditing: boolean;
  organizationOptions: { value: string; label: string }[];
  organizationsLoading: boolean;
  organizationSearchTerm: string;
  setOrganizationSearchTerm: (value: string) => void;
  insurancePlanOptions: {
    value: string;
    label: string;
    display?: string;
  }[];
  insurancePlansLoading: boolean;
  selectedOrganization: CoverageFormData["organization"];
}) {
  return (
    <Stack spacing={2.5}>
      <FormAutoComplete
        name="organization"
        control={control}
        label="Operadora do plano"
        required
        selectOptions={organizationOptions}
        loading={organizationsLoading}
        noOptionsText="Nenhuma operadora encontrada"
        onInputChange={(_, value) => {
          setOrganizationSearchTerm(value);
        }}
        disabled={isEditing}
      />

      <FormAutoComplete
        name="insurancePlan"
        control={control}
        label="Nome do plano"
        required
        selectOptions={insurancePlanOptions}
        loading={insurancePlansLoading}
        disabled={!selectedOrganization?.value || isEditing}
        noOptionsText={
          !selectedOrganization?.value
            ? "Selecione a operadora primeiro"
            : "Nenhum plano encontrado"
        }
      />

      <FormTextInput
        control={control}
        label="Número impresso na carteirinha"
        name="cardNumber"
        required
        placeholder="Ex.: 1234 5678 9012 3456"
      />
    </Stack>
  );
}

export function CoverageModal({
  open,
  isEditing,
  patientId,
  initialData,
  onClose,
  onSuccess,
}: CoverageModalProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");

  const {
    organizations,
    loading: organizationsLoading,
    fetchOrganizations,
  } = useOrganizations();

  const {
    insurancePlans,
    loading: insurancePlansLoading,
    fetchInsurancePlans,
    clearInsurancePlans,
  } = useInsurancePlans();

  const {
    control,
    handleSubmit,
    watch,
    reset,
  } = useForm<CoverageFormData>({
    defaultValues: initialData || {
      organization: null,
      insurancePlan: null,
      cardNumber: "",
    },
  });

  const selectedOrganization = watch("organization");

  useEffect(() => {
    if (open) {
      reset(
        initialData || {
          organization: null,
          insurancePlan: null,
          cardNumber: "",
        }
      );
      setOrganizationSearchTerm(initialData?.organization?.label || "");
    }
  }, [open, initialData, reset]);

  useEffect(() => {
    if (open) {
      fetchOrganizations({
        active: true,
        count: 50,
        type: "pay",
        content: organizationSearchTerm || initialData?.organization?.label,
      });
    }
  }, [open, organizationSearchTerm, fetchOrganizations, initialData?.organization?.label]);

  useEffect(() => {
    if (selectedOrganization?.value) {
      fetchInsurancePlans({
        ownedBy: selectedOrganization.value,
        count: 150,
      });
    } else {
      clearInsurancePlans();
    }
  }, [selectedOrganization?.value, fetchInsurancePlans, clearInsurancePlans]);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        value: org.id || "",
        label: org.name || org.id || "",
      })),
    [organizations]
  );

  const insurancePlanOptions = useMemo(
    () =>
      insurancePlans.map((plan) => ({
        value: plan.id || "",
        label: plan.name || "",
        display: plan.name || "",
      })),
    [insurancePlans]
  );

  const onSubmit = async (data: CoverageFormData) => {
    setIsSubmitting(true);
    try {
      await PostCoverage(data, { patientId });
      toast.success(
        isEditing
          ? "Carteirinha atualizada com sucesso!"
          : "Carteirinha cadastrada com sucesso!"
      );
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Erro ao salvar carteirinha");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isEditing ? "Editar carteirinha" : "Nova carteirinha";
  const subtitle = isEditing
    ? "Atualize os dados do seu cartão de saúde."
    : "Preencha os dados como aparecem na sua carteirinha física.";
  const primaryLabel = isSubmitting
    ? "Salvando..."
    : isEditing
      ? "Salvar alterações"
      : "Cadastrar carteirinha";
  const secondaryLabel = "Cancelar";

  const formFields = (
    <CoverageFormFields
      control={control}
      isEditing={isEditing}
      organizationOptions={organizationOptions}
      organizationsLoading={organizationsLoading}
      organizationSearchTerm={organizationSearchTerm}
      setOrganizationSearchTerm={setOrganizationSearchTerm}
      insurancePlanOptions={insurancePlanOptions}
      insurancePlansLoading={insurancePlansLoading}
      selectedOrganization={selectedOrganization}
    />
  );

  const actionButtons = (
    <Stack
      spacing={1.25}
      sx={{
        width: "100%",
        ...(isMobileLayout
          ? {
              px: 2,
              pt: 1.25,
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }
          : {}),
      }}
    >
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={isSubmitting}
        onClick={handleSubmit(onSubmit)}
        startIcon={
          isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined
        }
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 2.5,
          minHeight: 48,
        }}
      >
        {primaryLabel}
      </Button>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        disabled={isSubmitting}
        onClick={onClose}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 2.5,
          minHeight: 48,
        }}
      >
        {secondaryLabel}
      </Button>
    </Stack>
  );

  if (isMobileLayout) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={isSubmitting ? () => undefined : onClose}
        onOpen={() => undefined}
        disableSwipeToOpen
        swipeAreaWidth={0}
        ModalProps={{
          keepMounted: false,
          sx: { zIndex: theme.zIndex.modal + 2 },
          BackdropProps: {
            sx: { bgcolor: alpha(theme.palette.common.black, 0.45) },
          },
        }}
        PaperProps={{
          sx: {
            left: 0,
            right: 0,
            bottom: 0,
            maxHeight: "92dvh",
            borderRadius: "20px 20px 0 0",
            bgcolor: "background.paper",
            boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.14)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 99,
              bgcolor: "divider",
            }}
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            pb: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HealthAndSafety sx={{ fontSize: 18, color: "primary.main" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" lineHeight={1.35}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Fechar"
            sx={{ color: "text.secondary" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            px: 2,
            py: 2,
          }}
        >
          {formFields}
        </Box>

        {actionButtons}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack spacing={0.35}>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>{formFields}</DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, flexDirection: "column", gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={isSubmitting}
          onClick={handleSubmit(onSubmit)}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, minHeight: 44 }}
        >
          {primaryLabel}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          disabled={isSubmitting}
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, minHeight: 44 }}
        >
          {secondaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
